import React, { useEffect, useState } from "react";

import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import { yupResolver } from "@hookform/resolvers/yup";
import { ButtonVariant } from "Constant/button";
import useUpdateEffect from "hooks/useUpdateEffect";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import * as yup from "yup";

import StandartButton from "components/atoms/StandartButton";

import { CommonLandingPageSteps } from "../../AddEditLandingPage.types";
import StepContainer from "../../components/StepContainer";

const schema = yup.object().shape({
    paywall_activated: yup.boolean().required(),

    paywall_integration: yup.object().shape({
        provider: yup.string().when("paywall_activated", {
            is: true,
            then: (schema) => schema.required("Provider is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        client_token: yup.string().when("paywall_activated", {
            is: true,
            then: (schema) => schema.required("Client Token is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        api_key: yup.string().when("paywall_activated", {
            is: true,
            then: (schema) => schema.required("API Key is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        product_price_id: yup.string().when("paywall_activated", {
            is: true,
            then: (schema) => schema.required("Product Price ID is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
    }),
});

export default function PaywallIntegrationStep({
    landingPageDetails,
    onSave,
    setIsStepDirty,
    form,
}) {
    const isEditMode = !!useParams()?.id;

    const {
        control,
        handleSubmit,
        watch,
        reset,
        setValue,
        formState: { errors, isDirty },
    } = useForm({
        reValidateMode: "onChange",
        defaultValues: {
            paywall_integration: {
                provider: null,
                client_token: null,
                api_key: null,
                product_price_id: null,
            },
            paywall_activated: false,
            ...form,
        },
        resolver: yupResolver(schema),
    });

    const isEnabled = watch("paywall_activated");
    const [isAPIKeyInputDisabled, setIsAPIKeyInputDisabled] = useState(
        isEditMode &&
            landingPageDetails?.paywall_integration &&
            landingPageDetails?.paywall_activated,
    );

    const onSubmit = (data) => {
        const paywallData = data?.paywall_integration || {};
        const filteredPaywall = {};

        Object.entries(paywallData).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
                filteredPaywall[key] = value;
            }
        });

        onSave({
            stepName: CommonLandingPageSteps.PAYWALL_INTEGRATION,
            data: {
                ...data,
                paywall_integration: filteredPaywall,
            },
        });
    };

    useEffect(() => {
        reset(form, {
            keepDirty: false,
            keepTouched: false,
        });
    }, [form, reset]);

    useUpdateEffect(() => {
        setIsStepDirty(isDirty);
    }, [isDirty]);

    useUpdateEffect(() => {
        isDirty &&
            reset({
                paywall_activated: isEnabled,
                paywall_integration: {
                    provider: "",
                    client_token: "",
                    api_key: "",
                    product_price_id: null,
                },
            });
    }, [isEnabled]);

    return (
        <StepContainer
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            isDirty={isDirty}
        >
            {/* Step Header */}
            <Box sx={{ mb: { xs: 3, md: 3 } }}></Box>

            {/* Form Fields */}
            <Stack spacing={{ xs: 3, md: 4 }} sx={{ width: "100%" }}>
                {/* Paywall Toggle */}
                <Box
                    sx={{
                        p: { xs: 2, md: 3 },
                        backgroundColor: "#F8F9FA",
                        borderRadius: 3,
                        border: "1px solid #EDF0F6",
                    }}
                >
                    <Stack
                        alignItems="center"
                        gap={{ xs: 1, sm: 2 }}
                        direction="row"
                    >
                        <Controller
                            name="paywall_activated"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={field.value}
                                            sx={{
                                                "& .MuiSvgIcon-root": {
                                                    fontSize: {
                                                        xs: "20px",
                                                        md: "24px",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: {
                                                    xs: "16px",
                                                    md: "18px",
                                                },
                                            }}
                                        >
                                            Paywall Enabled
                                        </Typography>
                                    }
                                />
                            )}
                        />
                        <Tooltip title="What is a paywall? The point at which users are prompted to pay a subscription fee or make a one-time purchase to enjoy premium content or features.">
                            <IconButton size="small">
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>

                {/* Provider Selection */}
                <Box>
                    <FormControl
                        fullWidth
                        error={!!errors?.paywall_integration?.provider}
                    >
                        <Controller
                            name="paywall_integration.provider"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    labelId="provider-label"
                                    label="Provider"
                                    disabled={!isEnabled}
                                    {...field}
                                    required={isEnabled}
                                    sx={{
                                        height: { xs: "48px", md: "56px" },
                                        fontSize: { xs: "16px", md: "18px" },
                                    }}
                                >
                                    <MenuItem value="paddle">Paddle</MenuItem>
                                </Select>
                            )}
                        />
                        <FormHelperText
                            sx={{
                                fontSize: { xs: "12px", md: "14px" },
                                mt: { xs: 0.5, md: 1 },
                            }}
                        >
                            {errors?.paywall_integration?.provider?.message ||
                                "Choose the paywall provider you're integrating with."}
                        </FormHelperText>
                    </FormControl>
                </Box>

                {/* Client Token */}
                <Box>
                    <Controller
                        name="paywall_integration.client_token"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                label="Client Token"
                                fullWidth
                                {...field}
                                error={!!error}
                                helperText={
                                    error?.message ||
                                    "Your provider's client token for frontend authentication."
                                }
                                disabled={!isEnabled}
                                required={isEnabled}
                                sx={{
                                    "& .MuiInputBase-root": {
                                        height: { xs: "48px", md: "56px" },
                                        fontSize: { xs: "16px", md: "18px" },
                                    },
                                    "& .MuiFormHelperText-root": {
                                        fontSize: { xs: "12px", md: "14px" },
                                        mt: { xs: 0.5, md: 1 },
                                    },
                                }}
                            />
                        )}
                    />
                </Box>

                {/* API Key */}
                <Box>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        gap={2}
                        position="relative"
                        sx={{ alignItems: { xs: "stretch", sm: "center" } }}
                    >
                        {isAPIKeyInputDisabled && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "25%",
                                    left: "12px",
                                    zIndex: 1,
                                    backgroundColor: "#F5F5F5",
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: { xs: "12px", md: "14px" },
                                    color: "#666",
                                }}
                            >
                                *******************
                            </Box>
                        )}
                        <Controller
                            name="paywall_integration.api_key"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    label="API Key"
                                    fullWidth
                                    {...field}
                                    error={!!error}
                                    helperText={
                                        error?.message ||
                                        "Backend secret key used for API communication."
                                    }
                                    disabled={
                                        isAPIKeyInputDisabled || !isEnabled
                                    }
                                    sx={{
                                        flex: 1,
                                        "& .MuiInputBase-root": {
                                            height: { xs: "48px", md: "56px" },
                                            fontSize: {
                                                xs: "16px",
                                                md: "18px",
                                            },
                                        },
                                        "& .MuiFormHelperText-root": {
                                            fontSize: {
                                                xs: "12px",
                                                md: "14px",
                                            },
                                            mt: { xs: 0.5, md: 1 },
                                        },
                                    }}
                                />
                            )}
                        />
                        {isAPIKeyInputDisabled && (
                            <Tooltip title="Edit">
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "stretch",
                                        height: { xs: "36px", md: "3rem" },
                                        transform: "translateY(-.5rem)",
                                    }}
                                >
                                    <StandartButton
                                        onClick={() => {
                                            setIsAPIKeyInputDisabled(false);
                                            setValue(
                                                "paywall_integration.api_key",
                                                null,
                                            );
                                        }}
                                        iconButton
                                        variant={ButtonVariant.BLACK_OUTLINED}
                                        sx={{
                                            height: { xs: "36px", md: "3rem" },
                                            minWidth: {
                                                xs: "48px",
                                                md: "56px",
                                            },
                                        }}
                                    >
                                        <EditIcon />
                                    </StandartButton>
                                </Box>
                            </Tooltip>
                        )}
                    </Stack>
                </Box>

                {/* Product Price ID */}
                <Box>
                    <Controller
                        name="paywall_integration.product_price_id"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                label="Product Price ID"
                                fullWidth
                                {...field}
                                error={!!error}
                                disabled={!isEnabled}
                                required={isEnabled}
                                helperText={
                                    error?.message ||
                                    "Identifier for the product or price you want to charge users for."
                                }
                                sx={{
                                    "& .MuiInputBase-root": {
                                        height: { xs: "48px", md: "56px" },
                                        fontSize: { xs: "16px", md: "18px" },
                                    },
                                    "& .MuiFormHelperText-root": {
                                        fontSize: { xs: "12px", md: "14px" },
                                        mt: { xs: 0.5, md: 1 },
                                    },
                                }}
                            />
                        )}
                    />
                </Box>
            </Stack>
        </StepContainer>
    );
}
